import React, { useEffect, useState, useCallback } from 'react';
import { useUpdates, useUpdateMetadata } from '../../hooks/useUpdates';
import { UpdateNote } from '../../types/EducationHub';
import s from '../../styles/AdminAppUpdates.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  version: '', title: '', excerpt: '', description: '', content: '',
  category: '', releaseDate: '', coverImageUrl: '', isPublished: false,
};

// Normalizes any date-ish value from the API into yyyy-mm-dd for <input type="date">
const toDateInputValue = (value: unknown): string => {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminAppUpdates: React.FC = () => {
  const { fetchAdminUpdates, createUpdate, updateUpdateNote, deleteUpdate } = useUpdates();
  const { categories, fetchCategories } = useUpdateMetadata();

  const [updates, setUpdates] = useState<UpdateNote[]>([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoadingUpdates(true);
    try {
      const list = await fetchAdminUpdates();
      setUpdates(list);
    } finally {
      setLoadingUpdates(false);
    }
  }, [fetchAdminUpdates]);

  useEffect(() => { reload(); fetchCategories(); }, [reload, fetchCategories]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleEdit = (update: UpdateNote) => {
    setEditingId(update._id);
    setForm({
      version: update.version ?? '',
      title: update.title ?? '',
      excerpt: update.excerpt ?? '',
      description: update.description ?? '',
      content: update.content ?? '',
      category: update.category ?? '',
      releaseDate: toDateInputValue(update.releaseDate),
      coverImageUrl: update.coverImageUrl ?? '',
      isPublished: update.isPublished,
    });
    setFormError('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.version.trim() || !form.title.trim() || !form.content.trim() || !form.category.trim() || !form.releaseDate) {
      setFormError('Version, title, content, category, and release date are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        version: form.version.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || undefined,
        description: form.description.trim() || undefined,
        content: form.content,
        category: form.category.trim(),
        releaseDate: form.releaseDate,
        coverImageUrl: form.coverImageUrl.trim() || undefined,
        isPublished: form.isPublished,
      };
      if (editingId !== null) {
        await updateUpdateNote(editingId, payload);
      } else {
        await createUpdate(payload);
      }
      handleCancel();
      await reload();
      fetchCategories();
    } catch {
      setFormError('Failed to save update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this update?')) return;
    await deleteUpdate(id);
    await reload();
  };

  const handleTogglePublished = async (update: UpdateNote) => {
    await updateUpdateNote(update._id, { isPublished: !update.isPublished });
    await reload();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>📦 App Updates Management</h2>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>{editingId !== null ? 'Edit Update' : 'Add Update'}</h3>
        <div className={s.formGrid}>
          <label className={s.formLabel}>
            Version / Slug *
            <input className={s.input} placeholder="e.g. 2026-07-23" value={form.version}
              onChange={e => setForm(f => ({ ...f, version: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Title *
            <input className={s.input} placeholder="Update title" value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Category *
            <input className={s.input} placeholder="e.g. Features" value={form.category}
              list="update-categories"
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
            <datalist id="update-categories">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </label>
          <label className={s.formLabel}>
            Release Date *
            <input type="date" className={s.input} value={form.releaseDate}
              onChange={e => setForm(f => ({ ...f, releaseDate: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Cover Image URL
            <input className={s.input} placeholder="https://..." value={form.coverImageUrl}
              onChange={e => setForm(f => ({ ...f, coverImageUrl: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Excerpt (short summary shown on cards)
            <input className={s.input} value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Description
            <textarea className={s.textarea} rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Content *
            <textarea className={s.textarea} rows={6} value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
          </label>
          <label className={s.checkboxLabel}>
            <input type="checkbox" checked={form.isPublished}
              onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} />
            Published (visible to users)
          </label>
        </div>
        {formError && <p className={s.error}>{formError}</p>}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId !== null ? 'Update' : 'Add Update'}
          </button>
          {editingId !== null && (
            <button className={s.btnCancel} onClick={handleCancel}>Cancel</button>
          )}
        </div>
      </section>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>All Updates ({updates.length})</h3>
        {loadingUpdates ? <p className={s.loading}>Loading…</p> : (
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Release Date</th>
                  <th>Published</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {updates.map(update => (
                  <tr key={update._id}>
                    <td>{update.version}</td>
                    <td>{update.title}</td>
                    <td>{update.category ?? '—'}</td>
                    <td>{toDateInputValue(update.releaseDate) || '—'}</td>
                    <td>
                      <button
                        className={update.isPublished ? s.badgeActive : s.badgeInactive}
                        onClick={() => handleTogglePublished(update)}
                        title="Toggle published"
                      >
                        {update.isPublished ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className={s.actions}>
                      <button className={s.btnEdit} onClick={() => handleEdit(update)}>Edit</button>
                      <button className={s.btnDelete} onClick={() => handleDelete(update._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {updates.length === 0 && (
                  <tr><td colSpan={6} className={s.empty}>No updates yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminAppUpdates;
